import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Checkbox, DatePicker, Descriptions, Form, Input, InputNumber, Modal, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { SafetyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { useAccess } from '../../auth/AccessContext.jsx';
import { useLiveTopic } from '../../live/LiveContext.js';
import ActionIcon from '../../components/ActionIcon.jsx';
import StatusTag from '../../components/StatusTag.jsx';
import { formatInstant, formatMoney, formatUnits } from '../../format';
import { STATUS_KIND } from '../../status/statusHelp.js';
import { StepUpCancelled, useStepUp } from '../../stepup/useStepUp.js';
import {
    EXIT_FEE_BASIS, approveParams, conditionsOffered, describeChargeRate, describeDefaultCondition, describeEntryFee, describeExitFee,
    describeFeeDefaults, exitBasisOf, exitPreview,
    firstEffectiveDate, hasLapsed, isOfferedDate, isOwnProposal, lapsedText, modalFund, openConfigOf, proposalFormOf, proposalParams,
    proposeBlockedReason, rateToPercent, refusalOutcome, rejectParams, rejectTarget, replacedRowLabel
} from './feeConfig.js';

const { Text } = Typography;

const STATE_COLOUR = Object.freeze({ PROPOSED: 'gold', APPROVED: 'green', REJECTED: 'default' });

const dash = (value) => (value === null || value === undefined || value === '' ? '—' : value);
/**
 * IP-204: the Apply fees conditions, where they say something: a row's when
 * one is switched on (a row with none ticks every box, as before, and says
 * nothing more), a proposal's whenever it names them - switching them off
 * included. A proposal that names none keeps the ones in force.
 */
const conditionsLine = (fees) => {
    const d = fees.feeDefaults ?? null;
    const named = fees.proposalId ? Boolean(d) : Boolean(d?.entryAbove || d?.exitAbove);
    return named ? ' Apply fees by default: ' + describeFeeDefaults(d) : '';
};

const feesLine = (fees) => 'Exit fee: ' + describeExitFee(fees.exitFee) + '. Front-end fee: ' + describeEntryFee(fees.entryFee)
    + '. Transaction charge: ' + describeChargeRate(fees.transactionChargeRate ?? null) + '.' + conditionsLine(fees);

/** One fund_config row's rules, as Descriptions items. */
const configItems = (row) => [
    { key: 'exit', label: 'Exit fee', children: describeExitFee(row.exitFee), span: 3 },
    { key: 'entry', label: 'Front-end fee', children: describeEntryFee(row.entryFee), span: 3 },
    // IP-179: charged on the NAV file's charge base, added to the selling price and deducted from the buying price.
    { key: 'charge', label: 'Transaction charge rate', children: describeChargeRate(row.transactionChargeRate ?? null), span: 3 },
    // IP-204: whether a transaction's Apply fees box starts ticked - always, or only above an amount.
    { key: 'entryDefault', label: 'Apply front-end fee by default', children: describeDefaultCondition(row.feeDefaults?.entryAbove ?? null), span: 3 },
    { key: 'exitDefault', label: 'Apply exit fee by default', children: describeDefaultCondition(row.feeDefaults?.exitAbove ?? null), span: 3 },
    { key: 'cutoff', label: 'Cut-off (Colombo)', children: dash(row.cutoffTime) },
    { key: 'calendar', label: 'Calendar', children: dash(row.calendarCode) },
    { key: 'ref', label: 'Approval', children: dash(row.approvalRef) },
    { key: 'minSub', label: 'Min subscription', children: formatMoney(row.minSubscriptionAmount) },
    { key: 'minAdd', label: 'Min additional subscription', children: formatMoney(row.minAdditionalSubscriptionAmount) },
    { key: 'minRed', label: 'Min redemption units', children: formatUnits(row.minRedemptionUnits) },
    { key: 'minHold', label: 'Min holding units', children: formatUnits(row.minHoldingUnits) }
];

/**
 * The funds' rows (IP-98..IP-102): each fund's rules in force today, what is
 * scheduled, its open proposal, and the buttons the signed-in person may use.
 * The buttons are gated on exactly the claims the server checks
 * (permissions.js SCREEN_RULES.fundConfig; auth/screenGates.test.js): Propose
 * on core.fees.propose, Approve and Reject on core.fees.approve - and never on
 * the person's own proposal (MAKER_IS_CHECKER), nor Approve on a proposal
 * whose effective date has come (FEE_EFFECTIVE_DATE_TOO_EARLY). Rendered by
 * FundConfig below, and on its own by SettingsTab.test.js.
 */
export const FundFeesTable = ({ funds, loading = false, deciding = null, onPropose, onApprove, onReject }) => {
    const { canAct, principal } = useAccess();
    const canPropose = canAct('fundConfig', 'propose');
    const canApprove = canAct('fundConfig', 'approve');
    const canReject = canAct('fundConfig', 'reject');

    const columns = [
        {
            title: 'Fund',
            key: 'fund',
            width: 230,
            render: (_, f) => (
                <>
                    <Text strong>{f.fundId}</Text>
                    <div><Text type="secondary" className="fund-config-name">{f.name}</Text></div>
                </>
            )
        },
        { title: 'Cut-off', key: 'cutoff', width: 90, render: (_, f) => dash(f.current?.cutoffTime) },
        { title: 'Min subscription', key: 'minSub', width: 140, align: 'right', render: (_, f) => (f.current ? formatMoney(f.current.minSubscriptionAmount) : '—') },
        { title: 'Exit fee', key: 'exit', width: 230, render: (_, f) => (f.current ? describeExitFee(f.current.exitFee) : '—') },
        { title: 'Front-end fee', key: 'entry', width: 200, render: (_, f) => (f.current ? describeEntryFee(f.current.entryFee) : '—') },
        { title: 'Transaction charge', key: 'charge', width: 180, render: (_, f) => (f.current ? describeChargeRate(f.current.transactionChargeRate ?? null) : '—') },
        { title: 'In force since', key: 'since', width: 120, render: (_, f) => dash(f.current?.validFrom) },
        {
            title: 'Scheduled',
            key: 'scheduled',
            width: 120,
            // The fees are in the expanded row too; the tag takes focus so its tooltip reaches a keyboard.
            render: (_, f) => (f.scheduled.length
                ? f.scheduled.map((row) => (
                    <div key={row.configId}>
                        <Tooltip title={feesLine(row)}><Tag color="blue" tabIndex={0} aria-label={'From ' + row.validFrom + '. ' + feesLine(row)}>from {row.validFrom}</Tag></Tooltip>
                    </div>
                ))
                : '—')
        },
        {
            title: 'Proposal',
            key: 'proposal',
            width: 210,
            render: (_, f) => {
                const p = f.openProposal;
                if (!p) return '—';
                return (
                    <>
                        <StatusTag kind={STATUS_KIND.FEE_PROPOSAL} code="PROPOSED" color={STATE_COLOUR.PROPOSED} /> from {p.effectiveDate}
                        <div><Text type="secondary">by {p.proposedByName ?? p.proposedBy}</Text></div>
                        {hasLapsed(f, p) && (
                            <div>
                                <Tag color="red">Effective date has passed</Tag>
                                <div><Text type="secondary">{lapsedText({ own: isOwnProposal(p, principal?.uid), canReject })}</Text></div>
                            </div>
                        )}
                    </>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 230,
            render: (_, f) => {
                const p = f.openProposal;
                const own = isOwnProposal(p, principal?.uid);
                const blocked = proposeBlockedReason(f);
                const reasonId = 'propose-blocked-' + f.fundId;
                return (
                    <Space wrap size="small">
                        {canPropose && (
                            <Button size="small" icon={<SafetyOutlined />} disabled={Boolean(blocked) || Boolean(deciding)} onClick={() => onPropose(f)}
                                aria-describedby={blocked ? reasonId : undefined}>
                                Propose
                            </Button>
                        )}
                        {/* Why Propose is off, as text: a disabled button takes no focus, so a tooltip on it is never read. */}
                        {canPropose && blocked && !(p && own) && <Text type="secondary" id={reasonId}>{blocked}</Text>}
                        {p && own && <Text type="secondary" id={reasonId}>Waiting for another person to decide</Text>}
                        {p && !own && canApprove && !hasLapsed(f, p) && (
                            <Button size="small" type="primary" icon={<SafetyOutlined />} loading={deciding === p.proposalId}
                                disabled={Boolean(deciding)} onClick={() => onApprove(f, p)}>
                                Approve
                            </Button>
                        )}
                        {p && !own && canReject && (
                            <Button size="small" danger disabled={Boolean(deciding)} onClick={() => onReject(f, p)}>
                                Reject
                            </Button>
                        )}
                    </Space>
                );
            }
        }
    ];

    return (
        <Table
            className="fund-config-table"
            rowKey="fundId"
            size="middle"
            loading={loading}
            columns={columns}
            dataSource={funds}
            pagination={false}
            scroll={{ x: 1500 }}
            expandable={{ expandedRowRender: (f) => <FundDetail fund={f} /> }}
            locale={{ emptyText: 'No funds' }}
        />
    );
};

/**
 * Settings > Fund config (IP-98..IP-102), on the Console API: each fund's
 * dealing rules in force today (Asia/Colombo), the rows already scheduled,
 * and the four-eyes change to its fees.
 *
 * A person with core.fees.propose proposes a fund's exit fee - a percentage
 * of the buying price, charged on units held less than the holding period in
 * calendar months, or none where LIV's flag says the fund charges none - its
 * entry fee - a percentage of the amount, which allocation charges (IP-137) -
 * and the business day they take effect from, never today. Fees are
 * percentages only (IP-136): there is no LKR fee to choose.
 * A DIFFERENT person with core.fees.approve approves or rejects it. Each of
 * the three asks for a fresh authenticator code bound to exactly what is
 * sent, and the code dialog shows the server's own summary of the change.
 * Orders use the rules of their dealing date and a NAV run those of its
 * valuation date, so nothing reads a change before it takes effect.
 *
 * The page's copy can go stale - someone else proposes, decides, or a day
 * passes. The server judges every request on its own facts, so a refusal
 * that says the page was out of date (a 404 or 409) closes the open form or
 * dialog, is shown on the page, AND the funds are read again, so what the
 * person sees next is what the server holds (feeConfig.js refusalOutcome).
 * The screen keeps only ids for the open form and dialog, and finds their
 * fund and proposal in the funds as last read on every render (modalFund,
 * rejectTarget): a form never computes from a row the server has moved past.
 */
const FundConfig = () => {
    const { api } = useAccess();
    const { message } = App.useApp();
    const { withStepUp, stepUpDialog } = useStepUp();

    const [funds, setFunds] = useState([]);
    const [today, setToday] = useState(null);
    const [loading, setLoading] = useState(false);
    const [refreshedAt, setRefreshedAt] = useState(null);
    const [error, setError] = useState(null);
    const [proposeFor, setProposeFor] = useState(null); // a fundId
    const [rejecting, setRejecting] = useState(null); // { fundId, proposalId }
    const [deciding, setDeciding] = useState(null);
    const proposing = proposeFor ? modalFund(funds, proposeFor) : null;
    const rejectingTarget = rejectTarget(funds, rejecting);

    // A form or dialog whose fund or proposal the funds as last read no longer allow is closed.
    useEffect(() => {
        if (loading) return;
        if (proposeFor && !proposing) setProposeFor(null);
        if (rejecting && !rejectingTarget) setRejecting(null);
    }, [loading, proposeFor, proposing, rejecting, rejectingTarget]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const answer = await api.settings.fundConfig();
            setFunds(answer.funds);
            setToday(answer.today);
            setRefreshedAt(new Date());
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [api]);
    // Live screens (docs/live-sync/implementation-plan.md 4.6): re-read, exactly as Refresh does, when
    // a fee change was proposed, approved or rejected by someone else.
    useLiveTopic(['fundConfig'], load);

    useEffect(() => {
        load();
    }, [load]);

    /** A refusal for the person to read; when it says the page was stale, the form closes, the page says why and the funds are read again. */
    const refused = useCallback((e, show = setError) => {
        const outcome = refusalOutcome(e);
        if (outcome.closeModal) {
            setProposeFor(null);
            setRejecting(null);
        }
        (outcome.showOnPage ? setError : show)(e.message);
        if (outcome.reload) load();
    }, [load]);

    const approve = async (fund, proposal) => {
        setDeciding(proposal.proposalId);
        setError(null);
        try {
            const params = approveParams(proposal);
            await withStepUp('FUND_FEES_APPROVE', params,
                (stepUp) => api.settings.approve(proposal.proposalId, { payloadSha256: params.payloadSha256, stepUp }));
            message.success(fund.fundId + '\'s new fees are approved. They take effect from ' + proposal.effectiveDate + '.');
            load();
        } catch (e) {
            if (!(e instanceof StepUpCancelled)) refused(e);
        } finally {
            setDeciding(null);
        }
    };

    return (
        <div className="panel">
            <div className="fund-config-toolbar">
                <div className="fund-config-heading">
                    <h3 className="panel-title">Fund config</h3>
                    <div className="permission-info-banner fund-config-info">
                        <ActionIcon name="info" />
                        <span>
                            Each fund's dealing rules and fees as of {today ? '  ' + today : ''}. A fee change takes effect from a future business day: one person proposes it and a different person approves or rejects it, each with a code from their authenticator app. Orders use the fees of their dealing date, and a NAV run those of its valuation date. Times are Asia/Colombo. Last refreshed{' '}
                            {refreshedAt ? refreshedAt.toLocaleTimeString('en-GB', { timeZone: 'Asia/Colombo' }) : '—'}.
                        </span>
                    </div>
                </div>
                <Button className="refresh-button" icon={<ActionIcon name="refresh" />}
                    onClick={() => { setError(null); load(); }} loading={loading}>Refresh</Button>
            </div>
            {error && <Alert type="error" showIcon message={error} className="auth-alert" closable onClose={() => setError(null)} />}
            <FundFeesTable
                funds={funds}
                loading={loading}
                deciding={deciding}
                onPropose={(f) => { setError(null); setProposeFor(f.fundId); }}
                onApprove={approve}
                onReject={(f, p) => { setError(null); setRejecting({ fundId: f.fundId, proposalId: p.proposalId }); }}
            />
            {proposing && (
                <ProposeModal
                    key={proposing.fundId}
                    fund={proposing}
                    withStepUp={withStepUp}
                    onRefused={refused}
                    onClose={() => setProposeFor(null)}
                    onProposed={(proposal) => {
                        message.success('Proposed: ' + proposal.fundId + '\'s fees from ' + proposal.effectiveDate + '. A different person must approve them.');
                        setProposeFor(null);
                        load();
                    }}
                />
            )}
            {rejectingTarget && (
                <RejectModal
                    key={rejectingTarget.proposal.proposalId}
                    target={rejectingTarget}
                    withStepUp={withStepUp}
                    onRefused={refused}
                    onClose={() => setRejecting(null)}
                    onRejected={() => {
                        message.info(rejectingTarget.fund.fundId + '\'s proposed fees are rejected. Its fees are unchanged.');
                        setRejecting(null);
                        load();
                    }}
                />
            )}
            {stepUpDialog}
        </div>
    );
};

/** A fund's rules in full: today's, every scheduled row, the open proposal and the latest decisions. */
const FundDetail = ({ fund }) => {
    const p = fund.openProposal;
    const decisions = [
        { title: 'State', dataIndex: 'state', key: 'state', width: 110, render: (v) => <StatusTag kind={STATUS_KIND.FEE_PROPOSAL} code={v} color={STATE_COLOUR[v]} /> },
        { title: 'From', dataIndex: 'effectiveDate', key: 'effectiveDate', width: 110 },
        { title: 'Fees', key: 'fees', render: (_, row) => feesLine(row) },
        { title: 'Reason', dataIndex: 'reason', key: 'reason' },
        { title: 'Proposed', key: 'proposed', width: 190, render: (_, row) => <>{row.proposedByName ?? row.proposedBy}<div><Text type="secondary">{formatInstant(row.proposedAt)}</Text></div></> },
        {
            title: 'Decided',
            key: 'decided',
            width: 220,
            render: (_, row) => (
                <>
                    {row.decidedByName ?? row.decidedBy}
                    <div><Text type="secondary">{formatInstant(row.decidedAt)}</Text></div>
                    {row.rejectReason && <div><Text type="secondary">Rejected: {row.rejectReason}</Text></div>}
                </>
            )
        }
    ];
    return (
        <div style={{ display: 'grid', gap: 16 }}>
            {fund.current
                ? <Descriptions size="small" bordered column={3} title={'In force today, since ' + fund.current.validFrom} items={configItems(fund.current)} />
                : <Alert type="warning" showIcon message={'No dealing rules are in force for ' + fund.fundId + ' today.'} />}
            {fund.scheduled.map((row) => (
                <Descriptions key={row.configId} size="small" bordered column={3} title={'Scheduled from ' + row.validFrom} items={configItems(row)} />
            ))}
            {p && (
                <Descriptions size="small" bordered column={1} title="Proposed, waiting for a decision" items={[
                    { key: 'from', label: 'Takes effect from', children: p.effectiveDate },
                    { key: 'exit', label: 'Exit fee', children: describeExitFee(p.exitFee) },
                    { key: 'entry', label: 'Front-end fee', children: describeEntryFee(p.entryFee) },
                    { key: 'charge', label: 'Transaction charge rate', children: describeChargeRate(p.transactionChargeRate ?? null) },
                    { key: 'reason', label: 'Reason', children: p.reason },
                    { key: 'by', label: 'Proposed by', children: (p.proposedByName ?? p.proposedBy) + ', ' + formatInstant(p.proposedAt) },
                    { key: 'digest', label: 'Proposer\'s digest', children: <Text code copyable={{ text: p.payloadSha256 }}>{p.payloadSha256.slice(0, 16)}…</Text> }
                ]} />
            )}
            <Table
                rowKey="proposalId"
                size="small"
                columns={decisions}
                dataSource={fund.recentProposals}
                pagination={false}
                title={() => 'Latest fee changes decided'}
                locale={{ emptyText: 'No fee change decided yet' }}
            />
        </div>
    );
};

/**
 * FUND_FEES_PROPOSE: the fees, the date they take effect from and why. The
 * form starts from the fees of the row the change replaces; the preview is the
 * NAV run's own arithmetic on the latest buying price. Nothing is sent until
 * the person presses Propose and enters their code.
 *
 * Fees are percentages only (IP-136): the exit fee is the fund's one basis -
 * a percentage, or none where LIV's flag says so - so there is no basis to
 * choose, and the entry fee is a rate.
 */
const ProposeModal = ({ fund, withStepUp, onRefused, onClose, onProposed }) => {
    const { api } = useAccess();
    const [form] = Form.useForm();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const exitBasis = exitBasisOf(fund);
    const exitFeeBasis = exitBasis.value;
    const exitFeeValue = Form.useWatch('exitFeeValue', form);
    const exitFeeHoldingMonths = Form.useWatch('exitFeeHoldingMonths', form);
    // IP-204: each condition follows the form live - a front-end rate of 0 leaves nothing to tick.
    const entryFeeValue = Form.useWatch('entryFeeValue', form);
    const entryDefaultOn = Form.useWatch('entryDefaultOn', form);
    const exitDefaultOn = Form.useWatch('exitDefaultOn', form);
    const offered = conditionsOffered(fund, { exitFeeBasis, entryFeeValue });
    const first = firstEffectiveDate(fund);
    const open = openConfigOf(fund);
    const preview = exitPreview(fund, { exitFeeBasis, exitFeeValue });

    const submit = async (values) => {
        setError(null);
        const built = proposalParams(fund, values);
        if (built.error) {
            if (built.field) form.setFields([{ name: built.field, errors: [built.error] }]);
            else setError(built.error);
            return;
        }
        setBusy(true);
        try {
            const proposal = await withStepUp('FUND_FEES_PROPOSE', built.params, (stepUp) => api.settings.propose({ ...built.params, stepUp }));
            onProposed(proposal);
        } catch (e) {
            if (!(e instanceof StepUpCancelled)) onRefused(e, setError);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open
            width={640}
            title={'Propose fees for ' + fund.fundId}
            onCancel={onClose}
            onOk={() => form.submit()}
            okText="Propose"
            okButtonProps={{ loading: busy, icon: <SafetyOutlined /> }}
            maskClosable={false}
            rootClassName="lfoc-styles-wrapper"
        >
            <p>
                {fund.name}. {replacedRowLabel(fund)}: {open ? feesLine(open) : 'no rules.'} Nothing changes until a
                different person approves the proposal; proposing asks for a code from your authenticator app.
            </p>
            {error && <Alert type="error" showIcon message={error} className="auth-alert" closable onClose={() => setError(null)} />}
            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                disabled={busy}
                onFinish={submit}
                initialValues={{ ...proposalFormOf(fund), effectiveDate: first ? dayjs(first) : null }}
            >
                <Form.Item
                    name="effectiveDate"
                    label="Takes effect from"
                    rules={[{ required: true, message: 'Choose the date the fees take effect from' }]}
                    extra={first
                        ? 'The earliest is ' + first + ': a change never takes effect on the day it is made, nor on a day for which transactions were already taken, '
                            + 'as those were taken, and are priced, on the fees in force then. Weekends and holidays are not offered.'
                        : 'The dealing calendar holds no business day to change the fees from, so no change can take effect yet.'}
                >
                    <DatePicker format="YYYY-MM-DD" allowClear={false} disabledDate={(d) => !isOfferedDate(fund, d.format('YYYY-MM-DD'))} />
                </Form.Item>

                {exitFeeBasis === EXIT_FEE_BASIS.PERCENT
                    ? (
                        <Form.Item name="exitFeeValue" label="Exit fee rate" rules={[{ required: true, message: 'Enter the exit fee rate' }]}
                            extra="A percentage of the buying price, above 0 and at most 10.">
                            <Input suffix="%" placeholder="1.5" style={{ width: 200 }} />
                        </Form.Item>
                    )
                    : (
                        <Form.Item label="Exit fee">
                            <Text type="secondary">{exitBasis.label}</Text>
                        </Form.Item>
                    )}
                <Form.Item
                    name="exitFeeHoldingMonths"
                    label="Charged on units held less than"
                    hidden={exitFeeBasis === EXIT_FEE_BASIS.NONE}
                    rules={[{ required: true, message: 'Enter the holding period' }]}
                    extra="Calculated in calendar months from the date the units were purchased. Twelve months is one year."
                >
                    <InputNumber min={1} max={120} precision={0} suffix="months" style={{ width: 200 }} />
                </Form.Item>
                {exitFeeBasis !== EXIT_FEE_BASIS.NONE && (
                    preview.buyingPrice
                        ? preview.exitFeePrice && (
                            <div className="permission-info-banner fund-fee-preview-info">
                                <ActionIcon name="info" />
                                <span>
                                    {'At the latest buying price, ' + preview.buyingPrice + ' on ' + preview.dealingDate
                                        + ', a unit held less than ' + (exitFeeHoldingMonths ?? '?') + ' months is bought back at '
                                        + preview.exitFeePrice + ': a fee of LKR ' + preview.feePerUnit + ' a unit.'}
                                </span>
                            </div>
                        )
                        : (
                            <div className="permission-info-banner fund-fee-preview-info">
                                <ActionIcon name="info" />
                                <span>No price has been published for this fund yet: its exit-fee price is struck from its first price.</span>
                            </div>
                        )
                )}

                <Form.Item name="entryFeeValue" label="Front-end fee rate" rules={[{ required: true, message: 'Enter the front-end fee rate (0 for none)' }]}
                    extra="A percentage of the amount, charged on every subscription dealt from the date; 0 is no fee.">
                    <Input suffix="%" placeholder="0" style={{ width: 200 }} />
                </Form.Item>

                <Form.Item name="transactionChargeRate" label="Transaction charge rate"
                    rules={[{ required: true, message: 'Enter the transaction charge rate (0 for none)' }]}
                    extra="A percentage of the amount charged on every subscription from the effective date. A value of 0 means no fee.">
                    <Input suffix="%" placeholder="0" style={{ width: 200 }} />
                </Form.Item>

                <ApplyFeesConditions offered={offered} entryOn={entryDefaultOn} exitOn={exitDefaultOn} />

                <Form.Item name="reason" label="Reason" rules={[{ required: true, whitespace: true, message: 'Say why the fees change' }]}>
                    <Input maxLength={500} placeholder="For example: Board resolution 2026/14" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

/**
 * IP-204: the Propose form's "Apply fees by default" section - one condition
 * per fee, each a checkbox and the LKR amount a transaction must be MORE THAN
 * for its Apply fees box to start ticked. Unticked, the box always starts
 * ticked, as before. The reviewer can still change the box either way; this
 * only chooses where it starts.
 *
 * A condition on a fee the form does not charge is off and disabled, with the
 * reason as visible text (a disabled control takes no focus, so a tooltip
 * would never be read). Its amount input is enabled only while its box is
 * ticked; feeConfig.js proposalParams sends null for an unticked or disabled
 * one, whatever the input still holds. Exported for SettingsTab.test.js,
 * which renders it inside a Form.
 */
export const ApplyFeesConditions = ({ offered, entryOn, exitOn }) => {
    const row = (key, label, enabled, on, reason) => (
        <div className="fund-fee-condition" key={key}>
            <Space align="baseline" wrap>
                <Form.Item name={key + 'DefaultOn'} valuePropName="checked" noStyle>
                    <Checkbox disabled={!enabled} aria-describedby={reason ? key + '-condition-reason' : undefined}>{label}</Checkbox>
                </Form.Item>
                <Form.Item name={key + 'DefaultAbove'} noStyle>
                    <Input prefix="LKR" placeholder="100,000.00" style={{ width: 200 }} disabled={!enabled || !on}
                        aria-label={label + ': the amount'} />
                </Form.Item>
            </Space>
            {reason && <div><Text type="secondary" id={key + '-condition-reason'}>{reason}</Text></div>}
        </div>
    );
    return (
        <Form.Item label="Apply fees by default"
            extra={'Ticked: a transaction\'s Apply fees box starts ticked only when its amount is more than the amount - equal or below, it starts '
                + 'unticked. A transaction by units is valued at its price first. Unticked: the box always starts ticked. The reviewer can still '
                + 'change the box either way.'}>
            {row('entry', 'Subscriptions and switch-ins: tick only when the amount is more than', offered.entry.enabled, entryOn, offered.entry.reason)}
            {row('exit', 'Redemptions and switch-outs: tick only when the amount is more than', offered.exit.enabled, exitOn, offered.exit.reason)}
        </Form.Item>
    );
};

/** FUND_FEES_REJECT: another person turns a proposal down, with a reason and a code. The fund's fees stay as they are. */
const RejectModal = ({ target, withStepUp, onRefused, onClose, onRejected }) => {
    const { api } = useAccess();
    const [form] = Form.useForm();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const { fund, proposal } = target;

    const submit = async ({ reason }) => {
        setError(null);
        const built = rejectParams(proposal, reason);
        if (built.error) {
            form.setFields([{ name: 'reason', errors: [built.error] }]);
            return;
        }
        setBusy(true);
        try {
            await withStepUp('FUND_FEES_REJECT', built.params,
                (stepUp) => api.settings.reject(proposal.proposalId, { reason: built.params.reason, stepUp }));
            onRejected();
        } catch (e) {
            if (!(e instanceof StepUpCancelled)) onRefused(e, setError);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open
            title={'Reject the proposed fees for ' + fund.fundId + '?'}
            onCancel={onClose}
            onOk={() => form.submit()}
            okText="Reject"
            okButtonProps={{ danger: true, loading: busy, icon: <SafetyOutlined /> }}
            maskClosable={false}
            rootClassName="lfoc-styles-wrapper"
        >
            <p>
                From {proposal.effectiveDate}: {feesLine(proposal)} Proposed by {proposal.proposedByName ?? proposal.proposedBy}, because: {proposal.reason}
            </p>
            {error && <Alert type="error" showIcon message={error} className="auth-alert" closable onClose={() => setError(null)} />}
            <Form form={form} layout="vertical" requiredMark={false} disabled={busy} onFinish={submit}>
                <Form.Item name="reason" label="Reason" rules={[{ required: true, whitespace: true, message: 'Say why it is rejected' }]}>
                    <Input maxLength={500} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default FundConfig;
